import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Wallet, TrendingDown, Gift, ArrowRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useState } from 'react';

export const WalletTeaser = () => {
  const navigate = useNavigate();
  const [depositAmount, setDepositAmount] = useState(200);

  const calculateBonus = (amount: number) => {
    if (amount >= 500) return amount * 0.15;
    if (amount >= 200) return amount * 0.10;
    return 0;
  };

  const bonus = calculateBonus(depositAmount);
  const total = depositAmount + bonus;

  return (
    <section className="py-12 bg-gradient-to-br from-primary/5 to-accent/5">
      <div className="container mx-auto px-4">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-8">
            <Badge className="mb-3">
              <Gift className="h-3 w-3 mr-1" />
              Save More with Prepayment
            </Badge>
            <h2 className="text-3xl md:text-4xl font-bold mb-3">
              Therapy Wallet: Prepay & Save Up to 15%
            </h2>
            <p className="text-lg text-muted-foreground">
              Add funds to your wallet and get bonus credit on every deposit
            </p>
          </div>

          <Card className="border-2 border-primary/20 shadow-xl">
            <CardHeader className="border-b bg-gradient-to-r from-primary/5 to-accent/5">
              <CardTitle className="flex items-center gap-2">
                <Wallet className="h-5 w-5 text-primary" />
                Savings Calculator
              </CardTitle>
            </CardHeader>
            <CardContent className="p-6">
              <div className="grid md:grid-cols-2 gap-6">
                {/* Left: Deposit Options */}
                <div className="space-y-4">
                  <p className="text-sm font-medium text-muted-foreground mb-4">
                    Choose your deposit amount:
                  </p>
                  
                  {[
                    { amount: 100, bonus: 0, label: 'Starter' },
                    { amount: 200, bonus: 10, label: 'Popular', highlight: true },
                    { amount: 500, bonus: 15, label: 'Best Value' },
                    { amount: 1000, bonus: 15, label: 'Premium' }
                  ].map((option) => (
                    <div
                      key={option.amount}
                      onClick={() => setDepositAmount(option.amount)}
                      className={`relative cursor-pointer p-4 border-2 rounded-lg transition-all hover:shadow-md ${
                        depositAmount === option.amount
                          ? 'border-primary bg-primary/5'
                          : 'border-border hover:border-primary/50'
                      } ${option.highlight ? 'ring-2 ring-primary/20' : ''}`}
                    >
                      {option.highlight && (
                        <Badge className="absolute -top-2 -right-2 text-xs">
                          Most Popular
                        </Badge>
                      )}
                      <div className="flex items-center justify-between">
                        <div>
                          <div className="font-bold text-lg">${option.amount}</div>
                          <div className="text-sm text-muted-foreground">{option.label}</div>
                        </div>
                        {option.bonus > 0 && (
                          <Badge variant="secondary" className="text-green-600">
                            +{option.bonus}% Bonus
                          </Badge>
                        )}
                      </div>
                    </div>
                  ))}
                </div>

                {/* Right: Savings Breakdown */}
                <div className="flex flex-col justify-center">
                  <div className="bg-muted/50 rounded-lg p-6 space-y-4">
                    <div className="flex justify-between items-center pb-3 border-b">
                      <span className="text-muted-foreground">Deposit Amount:</span>
                      <span className="text-xl font-bold">${depositAmount}</span>
                    </div>
                    
                    {bonus > 0 && (
                      <div className="flex justify-between items-center pb-3 border-b">
                        <span className="text-muted-foreground">Bonus Credit:</span>
                        <span className="text-xl font-bold text-green-600">+${bonus}</span>
                      </div>
                    )}
                    
                    <div className="flex justify-between items-center pt-2">
                      <span className="text-lg font-semibold">Total Credit:</span>
                      <span className="text-3xl font-bold text-primary">${total}</span>
                    </div>

                    <div className="bg-green-50 border border-green-200 rounded-lg p-3 mt-4">
                      <div className="flex items-center gap-2 text-green-700">
                        <TrendingDown className="h-4 w-4" />
                        <span className="text-sm font-medium">
                          You save ${bonus} with this deposit
                        </span>
                      </div>
                    </div>
                  </div>

                  <Button 
                    size="lg" 
                    className="w-full mt-6 group"
                    onClick={() => navigate('/client-portal')}
                  >
                    Add ${depositAmount} to Wallet
                    <ArrowRight className="ml-2 h-4 w-4 group-hover:translate-x-1 transition-transform" />
                  </Button>

                  <p className="text-xs text-center text-muted-foreground mt-3">
                    No expiration • Use for any session • Fully refundable
                  </p>
                </div>
              </div>

              {/* Benefits */}
              <div className="grid sm:grid-cols-3 gap-4 mt-6 pt-6 border-t">
                <div className="text-center">
                  <div className="text-2xl mb-1">💰</div>
                  <div className="text-sm font-medium">Save Money</div>
                  <div className="text-xs text-muted-foreground">Up to 15% bonus</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl mb-1">⚡</div>
                  <div className="text-sm font-medium">Instant Booking</div>
                  <div className="text-xs text-muted-foreground">No payment delays</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl mb-1">🔒</div>
                  <div className="text-sm font-medium">Secure & Safe</div>
                  <div className="text-xs text-muted-foreground">100% protected</div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </section>
  );
};
